/**
 * Dashboard — Métricas con Recharts.
 */
import React, { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import { useAppStore } from '../../store';
import { metricsApi, agentsApi, defectsApi } from '../../services/api';
import type { DashboardMetrics, Agent, Defect } from '../../types';

const COLORS = {
  passed: '#1D9E75', failed: '#E24B4A', skipped: '#BA7517',
  pending: '#888780', primary: '#3B8BD4', purple: '#7F77DD',
};
const PIE_COLORS = [COLORS.passed, COLORS.failed, COLORS.skipped, COLORS.pending];

function MetricCard({ label, value, subtitle, trend, color }: {
  label: string; value: string | number; subtitle?: string; trend?: number; color?: string;
}) {
  return (
    <div style={{ padding: '20px 24px', borderRadius: 14, border: '1px solid var(--color-border-tertiary)', background: 'var(--color-background-primary)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: 'var(--color-text-tertiary)', marginBottom: 8, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 700, color: color ?? 'var(--color-text-primary)', lineHeight: 1, marginBottom: 4 }}>{value}</div>
      {subtitle && <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{subtitle}</div>}
      {trend !== undefined && (
        <div style={{ fontSize: 11, marginTop: 8, color: trend >= 0 ? COLORS.passed : COLORS.failed, fontWeight: 600 }}>
          {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% vs semana anterior
        </div>
      )}
    </div>
  );
}

const STALE_MS = 2 * 60 * 1000; // 2 minutos — igual que AgentsModule

function resolveStatus(agent: Pick<Agent, 'status' | 'last_heartbeat'>): string {
  const stale = agent.last_heartbeat
    ? Date.now() - new Date(agent.last_heartbeat).getTime() > STALE_MS
    : false;
  return stale && agent.status !== 'busy' ? 'offline' : agent.status;
}

function timeAgo(iso?: string | null): string {
  if (!iso) return 'sin heartbeat';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `hace ${diff}s`;
  if (diff < 3600) return `hace ${Math.floor(diff / 60)}min`;
  return `hace ${Math.floor(diff / 3600)}h`;
}

function AgentStatusBar({ agents }: { agents: Agent[] }) {
  const STATUS_CFG: Record<string, { color: string; label: string }> = {
    online:  { color: COLORS.passed,  label: 'Disponible' },
    busy:    { color: COLORS.primary, label: 'Ocupado' },
    offline: { color: COLORS.pending, label: 'Offline' },
    error:   { color: COLORS.failed,  label: 'Error' },
  };
  return (
    <div style={{ padding: '16px 20px', borderRadius: 14, border: '1px solid var(--color-border-tertiary)', background: 'var(--color-background-primary)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: 'var(--color-text-tertiary)', marginBottom: 14, textTransform: 'uppercase' }}>Agentes</div>
      {agents.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)', margin: 0 }}>Sin agentes registrados</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {agents.map(agent => {
            const effective = resolveStatus(agent);
            const cfg = STATUS_CFG[effective] ?? STATUS_CFG.offline;
            return (
              <div key={agent.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: 'var(--color-background-secondary)' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.color, flexShrink: 0,
                  boxShadow: effective === 'busy' ? `0 0 0 3px ${cfg.color}33` : effective === 'online' ? `0 0 0 2px ${cfg.color}44` : 'none' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{agent.name}</div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>{timeAgo(agent.last_heartbeat)} · {agent.platform}</div>
                </div>
                <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: cfg.color + '22', color: cfg.color, flexShrink: 0 }}>{cfg.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number; color: string }[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--color-background-primary)', border: '1px solid var(--color-border-secondary)', fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--color-text-primary)' }}>{label}</div>
      {payload.map(p => <div key={p.name} style={{ color: p.color, marginBottom: 2 }}>{p.name}: <strong>{p.value}</strong></div>)}
    </div>
  );
}

export function Dashboard() {
  // useShallow evita el re-render infinito al comparar shallowly
  const { metrics, setMetrics, metricsLoading, setMetricsLoading } = useAppStore(
    useShallow(s => ({
      metrics: s.metrics,
      setMetrics: s.setMetrics,
      metricsLoading: s.metricsLoading,
      setMetricsLoading: s.setMetricsLoading,
    }))
  );

  const [agents, setAgents] = useState<Agent[]>([]);
  const [defects, setDefects] = useState<Defect[]>([]);
  const [rangeDays, setRangeDays] = useState(30);

  const loadData = async () => {
    setMetricsLoading(true);
    try {
      const [m, a, defs] = await Promise.all([
        metricsApi.dashboard(rangeDays),
        agentsApi.list(),
        defectsApi.list(),
      ]);
      if (m) setMetrics(m);
      setAgents(a);
      setDefects(defs);
    } catch {
      // silently fail
    } finally {
      setMetricsLoading(false);
    }
  };

  // Polling de agentes cada 30s independiente del resto del dashboard
  useEffect(() => {
    const agentInterval = setInterval(async () => {
      try { const a = await agentsApi.list(); setAgents(a); } catch { /* silently fail */ }
    }, 30_000);
    return () => clearInterval(agentInterval);
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 60_000);
    return () => clearInterval(interval);
  }, [rangeDays]);

  if (metricsLoading && !metrics) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: 'var(--color-text-tertiary)' }}>Cargando métricas...</div>;
  }

  const m: DashboardMetrics = metrics ?? {
    total_cases: 0, total_executions: 0, pass_rate: 0, avg_duration_ms: 0, active_agents: 0,
    executed_cases: 0, coverage_pct: 0,
    executions_trend: [], pass_rate_trend: [], top_failing_cases: [],
    status_distribution: [], daily_executions: [],
  };

  const passRatePct = Math.round(m.pass_rate * 100);
  const avgDurationSec = (m.avg_duration_ms / 1000).toFixed(1);

  return (
    <div>
      <style>{`@keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }`}</style>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--color-text-primary)' }}>Dashboard</h2>
        <div style={{ display: 'flex', gap: 4 }}>
          {[7, 14, 30].map(days => (
            <button key={days} onClick={() => setRangeDays(days)} style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, border: `1px solid ${rangeDays === days ? COLORS.primary : 'var(--color-border-secondary)'}`, background: rangeDays === days ? '#E6F1FB' : 'transparent', color: rangeDays === days ? COLORS.primary : 'var(--color-text-secondary)', cursor: 'pointer', fontWeight: rangeDays === days ? 600 : 400, transition: 'all 0.15s' }}>
              {days}d
            </button>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
        <MetricCard label="Casos totales" value={m.total_cases} subtitle="en todos los sistemas" />
        <MetricCard label="Ejecuciones" value={m.total_executions} subtitle={`últimos ${rangeDays} días`} />
        <MetricCard label="Pass rate" value={`${passRatePct}%`} color={passRatePct >= 80 ? COLORS.passed : passRatePct >= 60 ? COLORS.skipped : COLORS.failed} trend={passRatePct - 70} />
        <MetricCard label="Duración media" value={`${avgDurationSec}s`} subtitle="por ejecución" />
        <MetricCard label="Agentes activos" value={agents.filter(a => resolveStatus(a) !== 'offline').length} color={COLORS.primary} />
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div style={{ padding: '20px 16px', borderRadius: 14, border: '1px solid var(--color-border-tertiary)', background: 'var(--color-background-primary)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 16 }}>Ejecuciones diarias</div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={m.daily_executions} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
              <defs>
                <linearGradient id="gPassed" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.passed} stopOpacity={0.15}/><stop offset="95%" stopColor={COLORS.passed} stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="gFailed" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.failed} stopOpacity={0.15}/><stop offset="95%" stopColor={COLORS.failed} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-tertiary)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--color-text-tertiary)' }} tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--color-text-tertiary)' }} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="passed" name="Pasaron" stroke={COLORS.passed} fill="url(#gPassed)" strokeWidth={2} />
              <Area type="monotone" dataKey="failed" name="Fallaron" stroke={COLORS.failed} fill="url(#gFailed)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div style={{ padding: '20px 16px', borderRadius: 14, border: '1px solid var(--color-border-tertiary)', background: 'var(--color-background-primary)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 16 }}>Tendencia de pass rate</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={m.pass_rate_trend} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-tertiary)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--color-text-tertiary)' }} tickFormatter={d => d.slice(5)} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: 'var(--color-text-tertiary)' }} tickFormatter={v => `${v}%`} />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="value" name="Pass rate" stroke={COLORS.purple} strokeWidth={2.5} dot={false} activeDot={{ r: 4, fill: COLORS.purple }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={{ padding: '20px 16px', borderRadius: 14, border: '1px solid var(--color-border-tertiary)', background: 'var(--color-background-primary)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 16 }}>Passed vs Failed</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={m.daily_executions.slice(-14)} margin={{ top: 5, right: 10, bottom: 5, left: -10 }} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-tertiary)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--color-text-tertiary)' }} tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--color-text-tertiary)' }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              <Bar dataKey="passed" name="Pasaron" fill={COLORS.passed} radius={[3, 3, 0, 0]} />
              <Bar dataKey="failed" name="Fallaron" fill={COLORS.failed} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {/* Distribution */}
          <div style={{ padding: '16px 14px', borderRadius: 14, border: '1px solid var(--color-border-tertiary)', background: 'var(--color-background-primary)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 14 }}>Distribución de estados</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <ResponsiveContainer width={130} height={130}>
                <PieChart>
                  <Pie data={m.status_distribution} dataKey="count" nameKey="status" cx="50%" cy="50%" innerRadius={36} outerRadius={58} paddingAngle={2}>
                    {m.status_distribution.map((_, idx) => <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
                {m.status_distribution.map((item, idx) => (
                  <div key={item.status} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: PIE_COLORS[idx % PIE_COLORS.length], flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', flex: 1, textTransform: 'capitalize' }}>{item.status}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-primary)' }}>{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Coverage */}
          <div style={{ padding: '16px 14px', borderRadius: 14, border: '1px solid var(--color-border-tertiary)', background: 'var(--color-background-primary)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 14 }}>Cobertura de pruebas</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ position: 'relative', width: 130, height: 130, flexShrink: 0 }}>
                <ResponsiveContainer width={130} height={130}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Ejecutados', value: m.executed_cases ?? 0 },
                        { name: 'Sin ejecutar', value: Math.max(0, m.total_cases - (m.executed_cases ?? 0)) },
                      ]}
                      dataKey="value" cx="50%" cy="50%" innerRadius={36} outerRadius={58} paddingAngle={2} startAngle={90} endAngle={-270}>
                      <Cell fill="#1D9E75" />
                      <Cell fill="var(--color-background-secondary)" />
                    </Pie>
                    <Tooltip formatter={(v, n) => [`${v} casos`, n]} />
                  </PieChart>
                </ResponsiveContainer>
                {/* Center label */}
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center', pointerEvents: 'none' }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--color-text-primary)', lineHeight: 1 }}>{m.coverage_pct ?? 0}%</div>
                </div>
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Ejecutados</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#1D9E75' }}>{m.executed_cases ?? 0}</div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>de {m.total_cases} casos</div>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: 'var(--color-background-secondary)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 3, background: '#1D9E75', width: `${m.coverage_pct ?? 0}%`, transition: 'width 0.6s ease' }} />
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                  {Math.max(0, m.total_cases - (m.executed_cases ?? 0))} sin ejecutar
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px 300px', gap: 20 }}>

        {/* Casos con más fallos — compacto con scroll */}
        <div style={{ padding: '20px', borderRadius: 14, border: '1px solid var(--color-border-tertiary)', background: 'var(--color-background-primary)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 16 }}>Casos con más fallos</div>
          {m.top_failing_cases.length === 0 ? (
            <p style={{ color: 'var(--color-text-tertiary)', fontSize: 13 }}>Sin datos de fallos en el período seleccionado.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 220, overflowY: 'auto' }}>
              {m.top_failing_cases.map((c, i) => (
                <div key={c.case_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderRadius: 10, background: 'var(--color-background-secondary)' }}>
                  <span style={{ width: 20, height: 20, borderRadius: 5, background: i < 3 ? '#FCEBEB' : '#F1EFE8', color: i < 3 ? '#A32D2D' : '#5F5E5A', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
                  <span style={{ flex: 1, fontSize: 12, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</span>
                  <span style={{ padding: '1px 7px', borderRadius: 5, background: '#FCEBEB', color: '#A32D2D', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{c.fail_count} fallos</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Defectos por estado — dona */}
        <div style={{ padding: '20px', borderRadius: 14, border: '1px solid var(--color-border-tertiary)', background: 'var(--color-background-primary)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 12 }}>Defectos por estado</div>
          {defects.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180, color: 'var(--color-text-tertiary)', fontSize: 12 }}>Sin defectos registrados</div>
          ) : (() => {
            const STATUS_COLORS: Record<string, string> = {
              open: '#E24B4A', in_progress: '#3B8BD4', not_reproducible: '#BA7517',
              rejected: '#888780', resolved: '#1D9E75', closed: '#7F77DD', wont_fix: '#C07A30',
            };
            const STATUS_LABEL: Record<string, string> = {
              open: 'Abierto', in_progress: 'En progreso', not_reproducible: 'No reproducible',
              rejected: 'Rechazado', resolved: 'Resuelto', closed: 'Cerrado', wont_fix: 'No se corregirá',
            };
            const counts: Record<string, number> = {};
            defects.forEach(d => { counts[d.status] = (counts[d.status] || 0) + 1; });
            const pieData = Object.entries(counts).map(([status, count]) => ({
              name: STATUS_LABEL[status] ?? status, value: count, status, color: STATUS_COLORS[status] ?? '#888',
            }));
            return (
              <div>
                <ResponsiveContainer width='100%' height={130}>
                  <PieChart>
                    <Pie data={pieData} cx='50%' cy='50%' innerRadius={36} outerRadius={58}
                      dataKey='value' paddingAngle={2}>
                      {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip formatter={(v: number, n: string) => [v, n]} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                  {pieData.map(d => (
                    <div key={d.status} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', flex: 1 }}>{d.name}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-primary)' }}>{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>

        <AgentStatusBar agents={agents} />
      </div>
    </div>
  );
}